import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from 'react-i18next';
import ListLoader from "../../common-components/ListLoader";
import { ToastContainer } from "react-toastify";
import useGetPhrAppDepartments from "./hooks/useGetPhrAppDepartments";
import useGetDoctorsByDepartment from "./hooks/useGetDoctorsByDepartment";
import useGetDoctorStaticById from "./hooks/useGetDoctorStaticById";
import CreateDoctorStaticModal from "./CreateDoctorStaticModal";
import ViewDoctorStaticModal from "./ViewDoctorStaticModal";

const DoctorInformationManagement = () => {
  const { t } = useTranslation();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditable, setIsEditable] = useState(false);
  const [currentDoctorStatic, setCurrentDoctorStatic] = useState({});
  const [viewDocId, setViewDocId] = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [isLoadingEditData, setIsLoadingEditData] = useState(false);

  const { allDepartments, isDepartmentLoading } = useGetPhrAppDepartments();
  const { doctors, isDoctorsLoading, getDoctorsByDepartment } = useGetDoctorsByDepartment();
  const { getDoctorStaticById, isDoctorStaticLoading } = useGetDoctorStaticById();

  // Filter active departments
  const activeDepartments = useMemo(() => {
    // Ensure allDepartments is an array
    if (!Array.isArray(allDepartments)) {
      return [];
    }
    return allDepartments.filter(dept => dept.isActive && !dept.isDelete);
  }, [allDepartments]);

  // Set first department as selected when departments are loaded
  useEffect(() => {
    if (activeDepartments.length > 0 && !selectedDepartment) {
      const firstDept = activeDepartments[0];
      setSelectedDepartment(firstDept.name || firstDept.departmentName || firstDept.deptname);
    }
  }, [activeDepartments, selectedDepartment]);

  // Fetch doctors when department is selected
  useEffect(() => {
    if (selectedDepartment) {
      getDoctorsByDepartment(selectedDepartment);
    }
  }, [selectedDepartment, getDoctorsByDepartment]);

  const handleDepartmentSelect = (departmentName) => {
    setSelectedDepartment(departmentName);
  };

  const handleCardClick = (doctor) => {
    // Only open view modal if static_data exists
    const hasStaticData = doctor.static_data && Object.keys(doctor.static_data).length > 0;
    if (hasStaticData) {
      const doctorData = doctor.static_data || doctor;
      if (doctorData.docId !== undefined) {
        setViewDocId(doctorData.docId);
        setIsViewModalOpen(true);
      }
    }
    // If no static_data, do nothing on card click (user should use Add button)
  };

  const handleEdit = async (doctor) => {
    // Get docId from doctor data
    const doctorData = doctor.static_data || doctor;
    const docId = doctorData.docId || doctor.docId;
    
    setIsLoadingEditData(true);
    try {
      if (docId !== undefined && docId !== null) {
        // Fetch the full static data from API
        const fullStaticData = await getDoctorStaticById(docId);
        if (fullStaticData) {
          setCurrentDoctorStatic(fullStaticData);
          setIsEditable(true);
          setIsCreateModalOpen(true);
        }
      } else {
        // Fallback: use available data if docId is not available
        setCurrentDoctorStatic(doctorData);
        setIsEditable(true);
        setIsCreateModalOpen(true);
      }
    } finally {
      setIsLoadingEditData(false);
    }
  };

  const handleAdd = (doctor) => {
    // Use doctor data from department API to pre-fill some fields
    const doctorData = {
      docId: doctor.docId,
      doctor_name: doctor.doctor || doctor.static_data?.doctor_name,
      photo_url: doctor.doctorImage || doctor.static_data?.photo_url,
      // Other fields will be empty for user to fill
    };
    setCurrentDoctorStatic(doctorData);
    setIsEditable(false); // false means it's in "add" mode
    setIsCreateModalOpen(true);
  };

  const handleView = (doctor) => {
    // Use static_data if available, otherwise use doctor data
    const doctorData = doctor.static_data || doctor;
    if (doctorData.docId !== undefined) {
      setViewDocId(doctorData.docId);
      setIsViewModalOpen(true);
    }
  };

  // Department Tabs Component
  const DepartmentTabs = () => {
    if (isDepartmentLoading) {
      return (
        <div className="flex justify-center py-4">
          <ListLoader />
        </div>
      );
    }

    if (activeDepartments.length === 0) {
      return (
        <div className="p-4 text-center text-gray-500">
          {t("No departments found")}
        </div>
      );
    }

    return (
      <div className="flex gap-2 overflow-x-auto pb-2 border-b border-gray-200">
        {activeDepartments.map((dept) => {
          const deptName = dept.name || dept.departmentName || dept.deptname || "";
          const isSelected = selectedDepartment === deptName;
          return (
            <button
              key={dept.id || dept.deptId || deptName}
              onClick={() => handleDepartmentSelect(deptName)}
              className={`px-4 py-2 rounded-t-lg font-medium whitespace-nowrap transition-colors ${
                isSelected
                  ? "bg-[#4C6AF7] text-white border-b-2 border-[#4C6AF7]"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              {deptName}
            </button>
          );
        })}
      </div>
    );
  };

  // Doctor Card Component
  const DoctorCard = ({ doctor }) => {
    const doctorData = doctor.static_data || doctor;
    const hasStaticData = doctor.static_data && Object.keys(doctor.static_data).length > 0;
    const doctorName = doctorData.doctor_name || doctor.doctor || doctorData.doctor || "--";
    const qualifications = doctorData.qualifications || "--";
    const languages = doctorData.languages || "--";
    const photoUrl = doctorData.photo_url || doctor.doctorImage || "--";

    return (
      <div
        className={`bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow border border-gray-200 overflow-hidden ${
          hasStaticData ? 'cursor-pointer' : 'cursor-default'
        }`}
        onClick={() => hasStaticData && handleCardClick(doctor)}
      >
        <div className="p-4">
          {/* Doctor Image */}
          <div className="flex justify-center mb-3">
            {photoUrl && photoUrl !== "--" ? (
              <img
                src={photoUrl}
                alt={doctorName}
                className="w-24 h-24 rounded-full object-cover border-2 border-gray-200"
                onError={(e) => {
                  e.target.src = "https://via.placeholder.com/96?text=No+Image";
                }}
              />
            ) : (
              <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center text-gray-400">
                <svg
                  className="w-12 h-12"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
              </div>
            )}
          </div>

          {/* Doctor Name */}
          <h3 className="text-lg font-semibold text-center mb-3 text-gray-800 truncate">
            {doctorName}
          </h3>

          {/* Qualifications */}
          <div className="mb-2">
            <p className="text-xs font-medium text-gray-500 mb-1">{t("Qualifications")}</p>
            <p className="text-sm text-gray-700 line-clamp-2">{qualifications}</p>
          </div>

          {/* Languages */}
          <div>
            <p className="text-xs font-medium text-gray-500 mb-1">{t("Languages")}</p>
            <p className="text-sm text-gray-700 line-clamp-2">{languages}</p>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 mt-4 pt-4 border-t border-gray-200" onClick={(e) => e.stopPropagation()}>
            {hasStaticData ? (
              <>
                <button
                  onClick={() => handleView(doctor)}
                  className="flex-1 px-3 py-2 bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100 text-sm font-medium transition-colors"
                >
                  {t("View")}
                </button>
                <button
                  onClick={() => handleEdit(doctor)}
                  disabled={isLoadingEditData}
                  className="flex-1 px-3 py-2 bg-[#4C6AF7] text-white rounded-md hover:bg-[#3d5ae5] disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors"
                >
                  {isLoadingEditData ? t("Loading...") : t("Edit")}
                </button>
              </>
            ) : (
              <button
                onClick={() => handleAdd(doctor)}
                className="w-full px-3 py-2 bg-[#4C6AF7] text-white rounded-md hover:bg-[#3d5ae5] text-sm font-medium transition-colors"
              >
                {t("Add")}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Doctors Grid Component
  const DoctorsGrid = () => {
    if (isDoctorsLoading) {
      return (
        <div className="flex justify-center py-12">
          <ListLoader />
        </div>
      );
    }

    if (doctors.length === 0) {
      return (
        <div className="p-8 text-center text-gray-500">
          {t("No doctors found in this department")}
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 p-6">
        {doctors.map((doctor, index) => {
          const doctorData = doctor.static_data || doctor;
          const key = doctorData.docId || doctor.docId || doctor.id || index;
          return <DoctorCard key={key} doctor={doctor} />;
        })}
      </div>
    );
  };

  return (
    <div className="m-5 text-sm text-primary">
      <ToastContainer />
      <div className="my-5">
  

        {/* Department Tabs */}
        <div className="mb-6">
          <DepartmentTabs />
        </div>

        {/* Doctors Grid */}
        <div className="bg-gray-50 rounded-lg">
          <DoctorsGrid />
        </div>
      </div>

      <CreateDoctorStaticModal
        isOpen={isCreateModalOpen}
        setIsOpen={setIsCreateModalOpen}
        getAllDoctorStatic={() => {
          // Refresh doctors for current department
          if (selectedDepartment) {
            getDoctorsByDepartment(selectedDepartment);
          }
        }}
        isEditable={isEditable}
        currentDoctorStatic={currentDoctorStatic}
        setCurrentDoctorStatic={setCurrentDoctorStatic}
        setIsEditable={setIsEditable}
      />

      <ViewDoctorStaticModal
        isOpen={isViewModalOpen}
        setIsOpen={setIsViewModalOpen}
        docId={viewDocId}
        onEdit={async (doctorData) => {
          // Fetch the full static data from API when editing from view modal
          setIsLoadingEditData(true);
          try {
            const docId = doctorData?.docId;
            if (docId !== undefined && docId !== null) {
              const fullStaticData = await getDoctorStaticById(docId);
              if (fullStaticData) {
                setCurrentDoctorStatic(fullStaticData);
                setIsEditable(true);
                setIsCreateModalOpen(true);
              }
            } else {
              // Fallback: use available data
              setCurrentDoctorStatic(doctorData);
              setIsEditable(true);
              setIsCreateModalOpen(true);
            }
          } finally {
            setIsLoadingEditData(false);
          }
        }}
      />
    </div>
  );
};

export default DoctorInformationManagement;

